import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDown, ChevronUp, AlertCircle, CheckCircle2 } from 'lucide-react';
import sampleData from '../data/AuditForm.json';
import MainLayout from '../nav/MainLayout';
import { Box } from '@mui/material';
import { styled } from "@mui/material/styles";

const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    minHeight: "50px",
 }));
  
const AuditForm = ({ data }) => {
    const [formData, setformData] = useState(sampleData);

  const [expandedSections, setExpandedSections] = useState({});
  const [formValues, setFormValues] = useState({});
  const [completedQuestions, setCompletedQuestions] = useState(new Set());

  // Initialize form values with pre-filled answers from the API
  useEffect(() => {
    const initialValues = {};
    const initialCompleted = new Set();

    formData.data.forEach(section => {
      section.subsection.forEach(subsection => {
        subsection.questions.forEach(question => {
          if (question.answer && question.answer.trim()) {
            initialValues[question.id] = question.answer;
            initialCompleted.add(question.id);
          }
        });
      });
    });

    setFormValues(initialValues);
    setCompletedQuestions(initialCompleted);
  }, [formData]);

  // Extract section names dynamically from data or use provided SectionName array
  const sectionNames = useMemo(() => {
    if (formData.SectionName && formData.SectionName.length > 0) {
      return formData.SectionName;
    }
    // Fallback to extracting from data if SectionName is not provided
    return formData.data.map(section => section.section);
  }, [formData]);

  // Get all required question IDs
  const requiredQuestions = useMemo(() => {
    const required = new Set();
    formData.data.forEach(section => {
      section.subsection.forEach(subsection => {
        subsection.questions.forEach(question => {
          if (question.required) {
            required.add(question.id);
          }
        });
      });
    });
    return required;
  }, [formData]);

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    return Array.from(requiredQuestions).every(questionId => {
      const value = formValues[questionId];
      return value && value.trim().length > 0;
    });
  }, [requiredQuestions, formValues]);

  // Get total question count
  const totalQuestions = useMemo(() => {
    return formData.data.reduce((total, section) => 
      total + section.subsection.reduce((subTotal, sub) => subTotal + sub.questions.length, 0), 0
    );
  }, [formData]);

  // Get section statistics
  const sectionStats = useMemo(() => {
    const stats = {};
    
    formData.data.forEach(section => {
      const sectionName = section.section;
      let total = 0;
      let completed = 0;
      let required = 0;
      let requiredCompleted = 0;

      section.subsection.forEach(subsection => {
        subsection.questions.forEach(question => {
          total++;
          if (formValues[question.id]?.trim()) {
            completed++;
          }
          if (question.required) {
            required++;
            if (formValues[question.id]?.trim()) {
              requiredCompleted++;
            }
          }
        });
      });

      stats[sectionName] = { total, completed, required, requiredCompleted };
    });

    return stats;
  }, [formData, formValues]);

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const handleInputChange = (questionId, value) => {
    setFormValues(prev => ({
      ...prev,
      [questionId]: value
    }));

    if (value.trim()) {
      setCompletedQuestions(prev => new Set(prev).add(questionId));
    } else {
      setCompletedQuestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });
    }
  };

  const handleSubmit = () => {
    if (!isFormValid) {
      alert('Please fill in all required fields before submitting.');
      return;
    }

    const submissionData = [];

    formData.data.forEach(section => {
      section.subsection.forEach(subsection => {
        subsection.questions.forEach(question => {
          const answer = formValues[question.id] || '';
          submissionData.push({
            questionId: question.id,
            questionText: question.text,
            answer: answer,
            required: question.required,
            section: section.section,
            subsection: subsection.subSectionName
          });
        });
      });
    });

    // Log the submission data (replace with your actual submission logic)
    console.log('Form Submission Data:', {
      formType: formData.form_type,
      version: formData.version,
      submittedAt: new Date().toISOString(),
      responses: submissionData,
      type: 'completed',
      sectionNames: sectionNames
    });

    // You can replace this with your actual API call
    alert('Audit form submitted successfully! Check console for submission data.');
  };

  const handleSaveDraft = () => {
    const submissionData = [];

    formData.data.forEach(section => {
      section.subsection.forEach(subsection => {
        subsection.questions.forEach(question => {
          const answer = formValues[question.id] || '';
          submissionData.push({
            questionId: question.id,
            questionText: question.text,
            answer: answer,
            required: question.required,
            section: section.section,
            subsection: subsection.subSectionName
          });
        });
      });
    });

    console.log('Form Draft Data:', {
      formType: formData.form_type,
      version: formData.version,
      savedAt: new Date().toISOString(),
      responses: submissionData,
      type: 'draft',
      sectionNames: sectionNames
    });

    alert('Draft saved successfully!');
  };

  const getSectionColor = (index) => {
    const colors = [
      'bg-teal-50 border-teal-200',
      'bg-blue-50 border-blue-200',
      'bg-indigo-50 border-indigo-200',
      'bg-purple-50 border-purple-200',
      'bg-green-50 border-green-200',
      'bg-orange-50 border-orange-200'
    ];
    return colors[index % colors.length];
  };

  const getHeaderColor = (index) => {
    const colors = [
      'bg-teal-500',
      'bg-teal-500',
      'bg-teal-500',
      'bg-teal-500',
      'bg-teal-500',
      'bg-teal-500'
    ];
    return colors[index % colors.length];
  };

  const getProgressColor = (completed, total) => {
    const percentage = total > 0 ? (completed / total) * 100 : 0;
    if (percentage === 100) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <Box
    className="mainLayout"
    sx={{ display: "flex", overflow: "hidden" }}
  >
    <MainLayout />

    <Box component="main">
              <DrawerHeader />

    <div className="max-w-8xl mx-auto p-6 bg-gray-50 min-h-screen">

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {formData.form_type} Audit Form
        </h1>
        <p className="text-gray-600">Version {formData.version}</p>
        
        {/* Section Overview */}
        <div className="mt-4 p-4 bg-white rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Section Overview</h3>
          <div className="grid grid-cols-1 gap-4 grid-cols-4 ">
            {sectionNames.map((sectionName, index) => {
              const stats = sectionStats[sectionName];
              if (!stats) return null;
              
              return (
                <div key={sectionName} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900 text-sm">{sectionName}</h4>
                    <div className={`w-3 h-3 rounded-full ${getHeaderColor(index).replace('bg-', 'bg-')}`}></div>
                  </div>
                  <div className="space-y-1 text-xs text-gray-600">
                    <div className={`${getProgressColor(stats.completed, stats.total)}`}>
                      Progress: {stats.completed}/{stats.total} questions
                    </div>
                    <div className={`${getProgressColor(stats.requiredCompleted, stats.required)}`}>
                      Required: {stats.requiredCompleted}/{stats.required} completed
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {formData.data.map((section, sectionIndex) => (
          <div
            key={section.section}
            className={`border rounded-lg overflow-hidden shadow-sm ${getSectionColor(sectionIndex)}`}
          >
            {/* Section Header */}
            <div
              className={`${getHeaderColor(sectionIndex)} text-white px-3 py-3 cursor-pointer transition-all duration-200 hover:opacity-90`}
              onClick={() => toggleSection(section.section)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="text-sm font-semibold">{section.section}</div>
                  <div className="text-sm opacity-90">
                    {sectionStats[section.section] && (
                      <span>
                        {sectionStats[section.section].completed}/{sectionStats[section.section].total} completed
                      </span>
                    )}
                  </div>
                </div>
                {expandedSections[section.section] ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </div>
            </div>

            {/* Section Content */}
            <div
              className={`transition-all duration-300 overflow-hidden ${
                expandedSections[section.section] ? 'max-h-none' : 'max-h-0'
              }`}
            >
              <div className="p-6 bg-white">
                {/* Auditor Prompt */}
                {section.prompt.header && (
                  <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h3 className="font-semibold text-amber-900 mb-3">
                      {section.prompt.header}
                    </h3>
                    <ul className="space-y-2 text-amber-800 px-1">
                      {section.prompt.list.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span className="text-sm leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Subsections */}
                {section.subsection.map((subsection, subsectionIndex) => (
                  <div key={subsectionIndex} className="mb-8 last:mb-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                      {subsection.subSectionName}
                    </h3>
                    
                    <div className="space-y-4">
                      {subsection.questions.map((question) => (
                        <div key={question.id} className="space-y-2">
                          <div className="flex items-start justify-between">
                            <label 
                              htmlFor={question.id}
                              className="text-sm font-medium text-gray-700 flex items-center"
                            >
                              <span className="mr-2">{question.text}</span>
                              {question.required && (
                                <span className="text-red-500 ml-1">*</span>
                              )}
                            </label>
                            {completedQuestions.has(question.id) && (
                              <CheckCircle2 className="w-4 h-4 text-green-500 mt-1" />
                            )}
                          </div>
                          
                          <div className="relative">
                            <textarea
                              id={question.id}
                              value={formValues[question.id] || ''}
                              onChange={(e) => handleInputChange(question.id, e.target.value)}
                              placeholder="Enter your response..."
                              className={`w-full min-h-[100px] p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical ${
                                question.required && !formValues[question.id]?.trim() 
                                  ? 'border-red-300 bg-red-50' 
                                  : 'border-gray-300'
                              }`}
                            />
                            {question.required && !formValues[question.id]?.trim() && (
                              <div className="absolute right-3 top-3">
                                <AlertCircle className="w-4 h-4 text-red-500" />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Form Actions */}
      <div className="mt-8 flex justify-between items-center bg-white p-6 rounded-lg shadow-sm border">
        <div className="text-sm text-gray-600">
          <div>Progress: {completedQuestions.size} of {totalQuestions} questions completed</div>
          <div className="mt-1">
            Required fields: {Array.from(requiredQuestions).filter(id => formValues[id]?.trim()).length} of {requiredQuestions.size} completed
          </div>
          <div className="mt-1 text-xs">
            Sections: {sectionNames.length} total
          </div>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handleSaveDraft}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Save Draft
          </button>
          <button 
            onClick={handleSubmit}
            disabled={!isFormValid}
            className={`px-6 py-2 rounded-md transition-colors ${
              isFormValid
                ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Submit Audit
          </button>
        </div>
      </div>

      {/* Validation Summary */}
      {!isFormValid && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700 font-medium">
              Please complete all required fields to submit the form
            </span>
          </div>
          <div className="mt-2 text-sm text-red-600">
            Missing required fields: {requiredQuestions.size - Array.from(requiredQuestions).filter(id => formValues[id]?.trim()).length}
          </div>
        </div>
      )}
    </div>
    </Box>
    </Box>

  );
};

export default AuditForm;